import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

export const UNASSIGNED_RESULTS_COUNT_QUERY_KEY = "unassignedResultsCount";

// Cheap on purpose: one row, and explicitly no stylesheets. The results list asks
// for `includeXsl=true` so the HL7 preview needs no second request, but a stylesheet
// is tens of kilobytes and this query wants only the total.
const COUNT_ONLY_PARAMS = "limit=1&offset=0&includeXsl=false&unassigned=true";

// An unmatched result is not a fast-moving event, and this runs on every page.
const REFETCH_INTERVAL_MS = 60000;

/**
 * How many results are sitting with no patient behind them.
 *
 * An unassigned result is on no chart, so nothing surfaces it anywhere in the app
 * except the Results screen itself, with the Unmatched switch turned on — which is
 * exactly the thing nobody turns on until they already suspect something is missing.
 * The count is what makes it noticeable without looking.
 *
 * Deliberately not scoped to the message types of the tab in view: a lab result
 * stranded while you are reading radiology is still stranded.
 */
export const useUnassignedResultsCount = ({ enabled = true } = {}) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [UNASSIGNED_RESULTS_COUNT_QUERY_KEY, tenantVal],
    queryFn: async () => {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/healthlink/common/results?${COUNT_ONLY_PARAMS}`
      );

      const payload = response.data?.data ?? response.data;

      return payload?.totalCount ?? payload?.results?.length ?? 0;
    },
    enabled: enabled && !!tenantVal,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    meta: { errorMessage: "Error fetching the unassigned results count" },
  });
};
